import * as React from 'react';
import { Query } from 'react-apollo';

import type { ApolloError } from 'apollo-client';
import gql from 'graphql-tag';

import user from 'js/lib/user';

import inServerContext from 'bundles/ssr/util/inServerContext';

export type CertificateImageDataState = {
  loading: boolean;
  error?: ApolloError;
  certificateImageUrl?: string;
  metaImageUrl?: string;
};

type Props = {
  code: string;
  children: (data: CertificateImageDataState) => JSX.Element;
};

type CertificateImageQueryData = {
  MetaImagesV1Resource: { elements: Array<{ url: string }> };
};

const CertificateImageQuery = gql`
  query CertificateImageQuery($code: String!) {
    MetaImagesV1Resource(code: $code)
      @rest(type: "MetaImagesV1Resource", path: "metaImages.v1/CERTIFICATE_LANDING_PAGE~{args.code}", method: "GET") {
      elements @type(name: "MetaImage") {
        url
      }
    }
  }
`;

export const shouldFetchCertImage = () => {
  /* 
    Due to long durations when certificate image needs to be created,
    we skip the call on server if a user is logged in and make the call on client.
    If a logged-out visitor comes to the page (like a bot from social media sites) we fetch on server -
    so meta image is present in HTML response. 

    We do this to reduce latency since the first user to view this page will be the owner (and logged-in).
    We make the call on client and then the image gets cached so if the owner shares the page, logged-out visitors 
    will hit the cached image and get a normal page response time.
    We always fetch on server for logged-out users so when bots visit the page (to scrape content for social sharing),
    they see the meta image in the response.
  */
  if (user.isAuthenticatedUser() && inServerContext) {
    return false;
  } else {
    return true;
  }
};

const CertificateImageDataProvider: React.FC<Props> = ({ code, children }: Props) => {
  return (
    <Query<CertificateImageQueryData, { code: string }>
      query={CertificateImageQuery}
      variables={{ code }}
      skip={!shouldFetchCertImage()}
      fetchPolicy="cache-and-network"
    >
      {({ loading, error, data }) => {
        const metaImagesResponse = data?.MetaImagesV1Resource;

        return children({
          loading,
          error,
          certificateImageUrl: metaImagesResponse?.elements[0]?.url,
          metaImageUrl: metaImagesResponse?.elements[0]?.url,
        });
      }}
    </Query>
  );
};

export default CertificateImageDataProvider;
